import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Button, Spinner } from '@w7/ui'
import {
  clearAllAuth,
  isAccessTokenExpired,
  logout,
  refreshAccessToken,
  rememberNext,
  resolveAuthPhase,
  type AuthPhase,
} from '@/auth/oidc'
import { startSessionWatch } from '@/auth/sessionWatch'
import { flashToast, toastCopy } from '@/ui/toaster'

/**
 * Gates the authenticated shell. Public routes (/login, /callback, /no-access)
 * render outside this component.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  // Resolve synchronously so pathname changes don’t flash “Checking session…”
  const [phase, setPhase] = useState<AuthPhase>(() => resolveAuthPhase())

  useEffect(() => {
    setPhase(resolveAuthPhase())
  }, [location.pathname, location.search])

  useEffect(() => {
    if (phase.phase !== 'expired') return
    let cancelled = false
    ;(async () => {
      const ok = await refreshAccessToken()
      if (cancelled) return
      if (ok && !isAccessTokenExpired()) {
        setPhase(resolveAuthPhase())
        return
      }
      clearAllAuth()
      flashToast(toastCopy.loginExpired)
      setPhase({ phase: 'anon' })
    })()
    return () => {
      cancelled = true
    }
  }, [phase.phase])

  // 1h absolute + 30m idle (also refreshes access tokens while active).
  useEffect(() => {
    if (phase.phase !== 'ready') return
    return startSessionWatch()
  }, [phase.phase])

  if (phase.phase === 'loading') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-loading">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'anon') {
    rememberNext(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }

  if (phase.phase === 'expired') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-refreshing">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Refreshing session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'no-access') {
    return <Navigate to="/no-access" replace />
  }

  if (phase.phase === 'ready') {
    return <>{children}</>
  }

  return (
    <div className="w7-auth w7-auth-center">
      <p>Unexpected auth state.</p>
      <Button onClick={() => logout()}>Sign out</Button>
    </div>
  )
}
